import { getFeaturedCourtLabel } from '@pickleballinc/lib/court-title';
import { formatDateTime } from '@pickleballinc/lib/format-dates';
import { getCDNURL } from '@pickleballinc/lib/get-cdn-url';
import type { Match } from '@pickleballinc/react-ui/types/Match';
import { formatRoundText } from '@pickleballinc/ui/lib/formatRoundText';

import { publicEnv } from '@/lib/env';
import type { MatchInfoV2 } from '@/modules/ticker/types';
import { IS_PRODUCTION } from '@/utils/helpers/constants';
import { shouldShowMatchStartTimeTicker } from '@/utils/helpers/shouldShowMatchStartTime';

const removeTParam = (url: string): string => {
  try {
    const parsed = new URL(url);
    parsed.searchParams.delete('t');
    return parsed.toString();
  } catch {
    return url;
  }
};

export interface FormatMatchInfoProps {
  matchInfos: MatchInfoV2[];
  imagesWidth: number;
  imagesHeight: number;
}

export function formatTickerMatches({
  matchInfos,
  imagesWidth,
  imagesHeight,
}: FormatMatchInfoProps) {
  if (matchInfos.length === 0) return [];

  const matches: Match[] = matchInfos.map((matchInfo) => {
    const teamOneScores = [matchInfo.teamOneGameOneScore];
    const teamTwoScores = [matchInfo.teamTwoGameOneScore];
    const gamesStatus: number[] = [+matchInfo.gameOneStatus];

    const teamOnePlayers = [
      {
        playerId: matchInfo.teamOnePlayerOneUuid,
        firstName: matchInfo.teamOnePlayerOneFirstName,
        lastName: matchInfo.teamOnePlayerOneLastName,
        suffixName: matchInfo.teamOnePlayerOneNameSuffix,
        image: getCDNURL(matchInfo.teamOnePlayerOnePicture, imagesWidth, imagesHeight),
      },
    ];
    const teamTwoPlayers = [
      {
        playerId: matchInfo.teamTwoPlayerOneUuid,
        firstName: matchInfo.teamTwoPlayerOneFirstName,
        lastName: matchInfo.teamTwoPlayerOneLastName,
        suffixName: matchInfo.teamTwoPlayerOneNameSuffix,
        image: getCDNURL(matchInfo.teamTwoPlayerOnePicture, imagesWidth, imagesHeight),
      },
    ];

    if (matchInfo.teamOnePlayerTwoUuid) {
      teamOnePlayers.push({
        playerId: matchInfo.teamOnePlayerTwoUuid,
        firstName: matchInfo.teamOnePlayerTwoFirstName,
        lastName: matchInfo.teamOnePlayerTwoLastName,
        suffixName: matchInfo.teamOnePlayerTwoNameSuffix,
        image: getCDNURL(matchInfo.teamOnePlayerTwoPicture, imagesWidth, imagesHeight),
      });
    }

    if (matchInfo.teamTwoPlayerTwoUuid) {
      teamTwoPlayers.push({
        playerId: matchInfo.teamTwoPlayerTwoUuid,
        firstName: matchInfo.teamTwoPlayerTwoFirstName,
        lastName: matchInfo.teamTwoPlayerTwoLastName,
        suffixName: matchInfo.teamTwoPlayerTwoNameSuffix,
        image: getCDNURL(matchInfo.teamTwoPlayerTwoPicture, imagesWidth, imagesHeight),
      });
    }

    if (matchInfo.scoreFormatGameBestOutOf > 1) {
      teamOneScores.push(matchInfo.teamOneGameTwoScore);
      teamTwoScores.push(matchInfo.teamTwoGameTwoScore);
      gamesStatus.push(+matchInfo.gameTwoStatus);
    }

    if (matchInfo.scoreFormatGameBestOutOf > 2) {
      teamOneScores.push(matchInfo.teamOneGameThreeScore);
      teamTwoScores.push(matchInfo.teamTwoGameThreeScore);
      gamesStatus.push(+matchInfo.gameThreeStatus);
    }

    if (matchInfo.scoreFormatGameBestOutOf > 3) {
      teamOneScores.push(matchInfo.teamOneGameFourScore);
      teamTwoScores.push(matchInfo.teamTwoGameFourScore);
      gamesStatus.push(+matchInfo.gameFourStatus);
    }

    if (matchInfo.scoreFormatGameBestOutOf > 4) {
      teamOneScores.push(matchInfo.teamOneGameFiveScore);
      teamTwoScores.push(matchInfo.teamTwoGameFiveScore);
      gamesStatus.push(+matchInfo.gameFiveStatus);
    }

    const formattedRoundText = formatRoundText({
      roundText: matchInfo.roundText,
      formatRound: true,
    });
    const showStartTime = shouldShowMatchStartTimeTicker(matchInfo);

    const streamingService = matchInfo.streamingServices[0] || undefined;

    const saturation = matchInfo.matchStatus === 4 ? -100 : 0;

    const isTeamLeagues = matchInfo.matchupUuid && matchInfo.matchupUuid !== '';

    const matchObject: Match = {
      reverseTeamRows: isTeamLeagues ? true : false,
      id: matchInfo.matchUuid,
      timezoneAbbreviation: showStartTime ? matchInfo.timezoneAbbreviation : '',
      matchStatus: matchInfo.matchStatus,
      matchCompletedType: matchInfo.matchCompletedType,
      currentServingNumber: matchInfo.currentServingNumber,
      server: matchInfo.server,
      serverFromTeam: matchInfo.serverFromTeam,
      team1: {
        players: [...teamOnePlayers],
        //percentage: Math.round(matchInfo.teamOneWinningPercentage * 10) / 10,
        scores: [...teamOneScores],
        isWinner: matchInfo.matchStatus === 4 && matchInfo.winner === 1,
      },
      team2: {
        players: [...teamTwoPlayers],
        //percentage: Math.round((100 - matchInfo.teamOneWinningPercentage) * 10) / 10,
        scores: [...teamTwoScores],
        isWinner: matchInfo.matchStatus === 4 && matchInfo.winner === 2,
      },
      bracketsURL: `${publicEnv.NEXT_PUBLIC_BRACKETS_URL}/tournaments/${matchInfo.moduleUuid}/events/${matchInfo.eventUuid}`,
      bracketsURLTarget: '_self',
      gamesStatus: gamesStatus,
      roundNumber: isTeamLeagues
        ? ''
        : formattedRoundText || (matchInfo.roundNumber ? `Round ${matchInfo.roundNumber}` : ''),
      courtName: getFeaturedCourtLabel(matchInfo.courtTitle),
      tournamentTitle: matchInfo.tournamentTitle,
      matchTimeStart: showStartTime
        ? formatDateTime(
            matchInfo?.localDateMatchCompleted ||
              matchInfo?.localDateMatchStart ||
              matchInfo?.localDateMatchPlannedStart,
            { type: 'time' }
          )
        : ' ',
      matchAbbreviation: matchInfo.matchAbbreviation,
      links: {
        matchStatsURL: isTeamLeagues
          ? `${publicEnv.NEXT_PUBLIC_PTL_URL}/team-leagues/${
              IS_PRODUCTION ? 'major-league-pickleball' : 'test_league_mlp'
            }/matchup/${matchInfo.matchupUuid}`
          : `/results/match/${matchInfo.matchUuid}`,
        liveURL:
          ((matchInfo.localDateMatchAssignedToCourt && !matchInfo.localDateMatchCompleted) ||
            matchInfo.matchStatus === 2) &&
          streamingService?.liveUrl
            ? removeTParam(streamingService.liveUrl)
            : undefined,
        archivedURL:
          matchInfo.matchStatus === 4 && streamingService?.archivedUrl
            ? streamingService.archivedUrl
            : undefined,
        highlightsURL:
          matchInfo.matchStatus === 4 && streamingService?.highlightsUrl
            ? streamingService.highlightsUrl
            : undefined,
        liveURLTarget: '_blank',
        archivedURLTarget: '_blank',
        highlightsURLTarget: '_blank',
      },
      sponsors:
        streamingService && streamingService.logoUrl
          ? {
              logoUrl: getCDNURL(streamingService.logoUrl, 100, 100, saturation),
              serviceId: streamingService.serviceId,
              serviceName: streamingService.serviceName,
            }
          : undefined,
    };

    return { ...matchObject, changeHappened: matchInfo.changeHappened };
  });

  return matches;
}
